import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import Cropper from 'react-easy-crop';
import { ref as storageRef, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ConfirmModal from './ConfirmModal';
import { Toast } from './Toast';
import { ItemCard, ItemCardCategory, ItemCardType } from '../types/itemCard';
import { ItemCardService } from '../services/itemCardService';
import { defaultSetService } from '../services/defaultSetService';
import { storage } from '../firebase/config';
import { User, Class } from '../types/alarm';
import { userDataService } from '../services/userDataService';
import LicenseLimitModal from './LicenseLimitModal';
import { cropToMaxWidthWebp, IMAGE_WEBP_EXT, IMAGE_WEBP_MIME } from '../utils/imageCompress';
import { buildUserStoragePath } from '../utils/userStoragePaths';

// 탭 타입 (ItemCardCategory + profileCard)
type TabType = ItemCardCategory | 'profileCard';

type CardImageTab = 'none' | 'default' | 'user';

const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const createHtmlImage = (src: string, useCors: boolean = true): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (e) => {
      console.error('이미지 로드 에러:', e, 'src:', src);
      reject(new Error('이미지 로드 실패'));
    });
    if (useCors) {
    image.setAttribute('crossOrigin', 'anonymous');
    }
    image.src = src;
  });

type CropAreaPixels = { x: number; y: number; width: number; height: number };

interface ItemCardSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  classId: string;
  user: User;
  onLicenseClick?: () => void;
}

const ItemCardSettingsModal: React.FC<ItemCardSettingsModalProps> = ({
  isOpen,
  onClose,
  userId,
  classId,
  user,
  onLicenseClick
}) => {
  // 다른 클래스 데이터 가져오기 버튼 활성화 상태 (기본값: off)
  const [isImportButtonEnabled, setIsImportButtonEnabled] = useState(false);
  
  // 로컬 스토리지 변경 감지
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('isImportButtonEnabled');
      setIsImportButtonEnabled(saved !== null ? saved === 'true' : false);
    };

    window.addEventListener('storage', handleStorageChange);
    // 같은 탭에서의 변경도 감지하기 위해 커스텀 이벤트 리스너 추가
    window.addEventListener('importButtonEnabledChanged', handleStorageChange);

    // 주기적으로 확인 (같은 탭에서의 변경 감지용)
    const interval = setInterval(() => {
      const saved = localStorage.getItem('isImportButtonEnabled');
      const currentValue = saved !== null ? saved === 'true' : false;
      setIsImportButtonEnabled(prev => {
        if (prev !== currentValue) {
          return currentValue;
        }
        return prev;
      });
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('importButtonEnabledChanged', handleStorageChange);
      clearInterval(interval);
    };
  }, []);
  const [activeTab, setActiveTab] = useState<TabType>('profileCard'); // 프리/베이직은 기본적으로 profileCard
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');
  const [levelupItems, setLevelupItems] = useState<ItemCard[]>([]);
  const [assignmentItems, setAssignmentItems] = useState<ItemCard[]>([]);
  const [storeItems, setStoreItems] = useState<ItemCard[]>([]);
  
  // 초기 데이터 저장 (변경사항 확인용)
  const [initialLevelupItems, setInitialLevelupItems] = useState<ItemCard[]>([]);
  const [initialAssignmentItems, setInitialAssignmentItems] = useState<ItemCard[]>([]);
  const [initialStoreItems, setInitialStoreItems] = useState<ItemCard[]>([]);
  const [initialProfileCardCostB, setInitialProfileCardCostB] = useState<number>(100);
  const [initialProfileCardCostC, setInitialProfileCardCostC] = useState<number>(100);
  
  // 모달 닫기 확인 팝업 상태
  const [isCloseConfirmModalOpen, setIsCloseConfirmModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ItemCard | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [itemCardService, setItemCardService] = useState<ItemCardService | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; category: ItemCardCategory } | null>(null);
  const [isNewItem, setIsNewItem] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCardImageModalOpen, setIsCardImageModalOpen] = useState(false);
  const [cardImageTab, setCardImageTab] = useState<CardImageTab>('default');
  const [selectedCardImage, setSelectedCardImage] = useState<string>(''); // 선택된 이미지 (임시)
  const lastCardImageClickTimeRef = useRef<number>(0);
  const lastCardImageClickValueRef = useRef<string | null>(null);

  // 사용자 카드 이미지(스토리지) 상태
  const [userCardImagesBySlot, setUserCardImagesBySlot] = useState<(string | null)[]>(() => Array(40).fill(null));
  const [, setIsLoadingUserCardImages] = useState(false);

  // 사용자 이미지 업로드/촬영/크롭 상태
  const [isUserImageSourceMenuOpen, setIsUserImageSourceMenuOpen] = useState(false);
  const [isUserCameraModalOpen, setIsUserCameraModalOpen] = useState(false);
  const [isUserCropModalOpen, setIsUserCropModalOpen] = useState(false);
  const [rawUserImageSrc, setRawUserImageSrc] = useState('');
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const userCropPixelsRef = useRef<CropAreaPixels | null>(null);
  const userFileInputRef = useRef<HTMLInputElement | null>(null);
  const userVideoRef = useRef<HTMLVideoElement | null>(null);
  const userCameraStreamRef = useRef<MediaStream | null>(null);
  const [isUploadingUserCardImage, setIsUploadingUserCardImage] = useState(false);
  const [replacingSlotIndex, setReplacingSlotIndex] = useState<number | null>(null); // 대체할 슬롯 인덱스
  const [addingSlotIndex, setAddingSlotIndex] = useState<number | null>(null); // 추가할 슬롯 인덱스 (빈 슬롯)
  const [deleteConfirmImageUrl, setDeleteConfirmImageUrl] = useState<string | null>(null); // 삭제 확인할 이미지 URL
  const [deleteConfirmSlotIndex, setDeleteConfirmSlotIndex] = useState<number | null>(null); // 삭제 확인할 슬롯 인덱스

  const [toastMessage, setToastMessage] = useState<string>('');
  const showToast = useCallback((msg: string) => setToastMessage(msg), []);
  
  // 아이템 카드 지급 여부 확인 상태 (itemCardId -> boolean)
  const [grantedItemCards, setGrantedItemCards] = useState<Set<string>>(new Set());
  const [, setIsCheckingGranted] = useState(false);
  
  // 다른 클래스 데이터 가져오기 관련 states
  const [showImportModal, setShowImportModal] = useState(false);
  const [availableClasses, setAvailableClasses] = useState<Class[]>([]);
  const [selectedSourceClassId, setSelectedSourceClassId] = useState<string>('');
  const [sourceLevelupItems, setSourceLevelupItems] = useState<ItemCard[]>([]);
  const [sourceAssignmentItems, setSourceAssignmentItems] = useState<ItemCard[]>([]);
  const [sourceStoreItems, setSourceStoreItems] = useState<ItemCard[]>([]);
  const [selectedItemCardIds, setSelectedItemCardIds] = useState<Set<string>>(new Set());
  const [isLoadingSourceItems, setIsLoadingSourceItems] = useState(false);
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);
  
  // 프로필 카드 가격 상태 (세트별)
  const [profileCardCostInputB, setProfileCardCostInputB] = useState<string>('100');
  const [profileCardCostInputC, setProfileCardCostInputC] = useState<string>('100');

  // 카드 이미지 옵션 (숫자 1~40)
  const cardImageOptions = Array.from({ length: 40 }, (_, i) => (i + 1).toString());

  // 숫자 문자열을 IconBasic 이미지 경로로 변환
  const getIconBasicImagePath = useCallback((numStr: string): string => {
    return `/CardIcon/IconBasic/IconBasic-${numStr}.png`;
  }, []);

  const isNumericCardImage = useCallback((v?: string): boolean => {
    if (!v) return false;
    return /^\d+$/.test(v);
  }, []);

  const userCardImageCount = useMemo(() => userCardImagesBySlot.filter(Boolean).length, [userCardImagesBySlot]);

  const loadUserCardImages = useCallback(async () => {
    if (!userId || !classId || !itemCardService) {
      console.log('⚠️ loadUserCardImages: 필수 정보 없음', { userId: !!userId, classId: !!classId, itemCardService: !!itemCardService });
      return;
    }
    setIsLoadingUserCardImages(true);
    try {
      // 저장된 순서 불러오기 (null 포함한 전체 40개 슬롯 배열)
      const savedOrder = await itemCardService.loadUserCardImageOrder();
      console.log('📖 저장된 순서 불러오기:', savedOrder.length, '개 슬롯 (null 포함)', savedOrder);

      // Storage에서 모든 이미지 파일 가져오기
      const folderRef = storageRef(storage, buildUserStoragePath(userId, classId, 'CardImage'));
      const result = await listAll(folderRef);
      
      // 모든 파일의 URL 가져오기
      const allFiles = await Promise.all(
        result.items.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return url;
        })
      );

      const next: (string | null)[] = Array(40).fill(null);

      // 저장된 순서가 있고 유효한 URL이 있으면 사용
      const hasSavedOrder = savedOrder.some(url => url !== null);
      
      if (hasSavedOrder) {
        // 저장된 순서를 그대로 사용 (null 포함)
        savedOrder.forEach((url, idx) => {
          if (idx < 40) {
            // 저장된 URL이 실제 파일 목록에 있는지 확인
            if (url && allFiles.includes(url)) {
              next[idx] = url;
            } else if (url === null) {
              // null은 그대로 유지
              next[idx] = null;
            }
            // 저장된 URL이 실제 파일 목록에 없으면 null로 처리 (파일이 삭제된 경우)
          }
        });

        // 저장된 순서에 없는 새 이미지들을 빈 슬롯에 추가
        const orderedUrls = new Set(savedOrder.filter((url): url is string => url !== null));
        for (const url of allFiles) {
          if (!orderedUrls.has(url)) {
            // 빈 슬롯 찾기
            const emptyIndex = next.findIndex(slot => slot === null);
            if (emptyIndex >= 0 && emptyIndex < 40) {
              next[emptyIndex] = url;
            }
          }
        }
      } else {
        // 저장된 순서가 없으면 타임스탬프로 정렬 (기존 로직)
      const filesWithTimestamp = await Promise.all(
        result.items.map(async (itemRef) => {
          const name = itemRef.name || '';
          const match = name.match(/^(\d+)\.(jpg|webp)$/i);
          if (!match) return null;
          const timestamp = Number(match[1]);
          if (!Number.isFinite(timestamp)) return null;
          const url = await getDownloadURL(itemRef);
          return { timestamp, url };
        })
      );

      const validFiles = filesWithTimestamp
        .filter((f): f is { timestamp: number; url: string } => f !== null)
        .sort((a, b) => a.timestamp - b.timestamp)
          .slice(0, 40);

      validFiles.forEach((file, idx) => {
        if (idx < 40) {
          next[idx] = file.url;
        }
      });
      }

      setUserCardImagesBySlot(next);
    } catch (e) {
      console.error('사용자 카드 이미지 로드 실패:', e);
      showToast('사용자 이미지를 불러오지 못했습니다.');
      setUserCardImagesBySlot(Array(40).fill(null));
    } finally {
      setIsLoadingUserCardImages(false);
    }
  }, [showToast, userId, classId, itemCardService]);

  // 카드 이미지 모달 오픈 시 초기 탭/데이터 로드
  useEffect(() => {
    if (!isCardImageModalOpen) return;
    // 모달이 열릴 때 현재 이미지를 선택 상태로 초기화
    if (editingItem) {
      const currentImage = editingItem.cardImage || '';
      setSelectedCardImage(currentImage);
      // 항상 기본 이미지 탭부터 시작하도록 설정
      setCardImageTab('default');
    } else {
      // 기본 탭으로 시작
      setCardImageTab('default');
    }
  }, [isCardImageModalOpen, editingItem]);

  useEffect(() => {
    if (!isCardImageModalOpen) return;
    if (cardImageTab !== 'user') return;
    void loadUserCardImages();
  }, [isCardImageModalOpen, cardImageTab, loadUserCardImages]);

  // 드래그 앤 드롭 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 1000, // 1초 이상 누르고 있어야 드래그 시작
        tolerance: 5, // 5px 이내의 움직임은 허용 (의도하지 않은 움직임 무시)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 사용자 이미지 드래그 앤 드롭 핸들러
  const handleUserImageDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeIndex = Number(active.id);
    const overIndex = Number(over.id);

    if (!Number.isFinite(activeIndex) || !Number.isFinite(overIndex)) return;
    if (activeIndex < 0 || activeIndex >= 40 || overIndex < 0 || overIndex >= 40) return;

    console.log('🔄 드래그 앤 드롭:', { activeIndex, overIndex, itemCardService: !!itemCardService });

    // 로컬 상태 업데이트
    const newOrder = arrayMove(userCardImagesBySlot, activeIndex, overIndex);
    setUserCardImagesBySlot(newOrder);

    // 서버에 순서 저장 (null 포함한 전체 배열)
    if (!itemCardService) {
      console.error('❌ itemCardService가 없습니다.');
      showToast('서비스 초기화에 실패했습니다.');
      return;
    }

    try {
      // 전체 배열을 그대로 저장 (null 포함)
      console.log('💾 저장할 순서:', newOrder.length, '개 슬롯 (null 포함)');
      await itemCardService.saveUserCardImageOrder(newOrder);
      console.log('✅ 사용자 이미지 순서 저장 완료');
    } catch (error) {
      console.error('❌ 사용자 이미지 순서 저장 실패:', error);
      showToast('이미지 순서 저장에 실패했습니다.');
    }
  }, [userCardImagesBySlot, itemCardService, showToast]);

  // SortableItem 컴포넌트
  const SortableUserCardImageSlot: React.FC<{
    id: number;
    url: string | null;
    index: number;
    isSelected: boolean;
    onSelect: () => void;
    isImageInUse: (url: string) => boolean;
    onDelete: (url: string, index: number) => void;
    onReplace: (index: number) => void;
    onAdd: (index: number) => void;
  }> = ({ id, url, index, isSelected, onSelect, isImageInUse, onDelete, onReplace, onAdd }) => {
    const [isPointerDown, setIsPointerDown] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ 
      id: id.toString(), 
      disabled: !url // url이 없을 때만 비활성화
    });

    // 포인터가 눌렸을 때 또는 드래그 중일 때 효과 적용
    const isActive = isPointerDown || isDragging;
    
    // 드래그 가능 상태 (포인터 다운)일 때: 확대 + 위로 이동
    // 실제 드래그 중일 때: 제자리에서 분리되는 느낌 (transform만 적용)
    const baseTransform = CSS.Transform.toString(transform);
    const dragTransform = isDragging
      ? baseTransform // 드래그 중에는 제자리에서 분리되는 느낌만
      : isPointerDown
      ? `${baseTransform} scale(1.05) translateY(-8px)` // 포인터 다운 시 확대 + 위로 이동
      : baseTransform;

    const style = {
      transform: dragTransform,
      transition: isDragging ? transition : 'transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease',
      opacity: isActive ? 0.9 : 1,
      zIndex: isActive ? 1000 : 1,
    };

    const handlePointerDown = (e: React.PointerEvent) => {
      if (url) {
        setIsPointerDown(true);
      }
    };

    const handlePointerUp = () => {
      setIsPointerDown(false);
    };

    const handleClick = (e: React.MouseEvent) => {
      // 드래그가 발생하지 않았을 때만 클릭 처리
      // @dnd-kit의 activationConstraint로 8px 이상 움직이면 드래그로 처리되므로
      // 클릭은 자연스럽게 구분됨
      if (!isDragging) {
        e.preventDefault();
        e.stopPropagation();
        if (url) {
        onSelect();
        } else {
          // 빈 슬롯 클릭 시 이미지 추가
          onAdd(index);
        }
      }
      setIsPointerDown(false);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (url) {
        onDelete(url, index);
      }
    };

    const handleReplaceClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onReplace(index);
    };

    const isInUse = url ? isImageInUse(url) : false;

    return (
      <UserCardImageSlot
        ref={setNodeRef}
        style={style}
        type="button"
        $isFilled={!!url}
        $isSelected={isSelected}
        $isDragging={isDragging}
        $isDragMode={isActive}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={url ? `사용자 이미지 ${index + 1}` : `빈 슬롯 ${index + 1}`}
        {...(url ? { ...attributes, ...listeners } : {})}
      >
        {url ? (
          <UserCardImageThumb src={url} alt={`사용자 이미지 ${index + 1}`} />
        ) : (
          <EmptySlotIcon>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" strokeDasharray="4 4" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
          </EmptySlotIcon>
        )}
        {url && isHovered && !isDragging && (
          <>
          <UserImageActionButton
              onClick={handleReplaceClick}
              $isReplace={true}
              $position="top-left"
              title="이미지 교체"
          >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </UserImageActionButton>
            {!isInUse && (
              <UserImageActionButton
                onClick={handleDeleteClick}
                $isReplace={false}
                $position="top-right"
                title="이미지 삭제"
              >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
          </UserImageActionButton>
            )}
          </>
        )}
      </UserCardImageSlot>
    );
  };

  // 사용자 카메라 모달 오픈 시 스트림 연결/해제
  useEffect(() => {
    if (!isUserCameraModalOpen) {
      if (userCameraStreamRef.current) {
        userCameraStreamRef.current.getTracks().forEach((t) => t.stop());
        userCameraStreamRef.current = null;
      }
      return;
    }

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        userCameraStreamRef.current = stream;
        if (userVideoRef.current) {
          userVideoRef.current.srcObject = stream;
          await userVideoRef.current.play();
        }
      } catch (e) {
        console.error('카메라 접근 실패:', e);
        setIsUserCameraModalOpen(false);
        showToast('카메라를 사용할 수 없습니다.');
      }
    })();

    return () => {
      if (userCameraStreamRef.current) {
        userCameraStreamRef.current.getTracks().forEach((t) => t.stop());
        userCameraStreamRef.current = null;
      }
    };
  }, [isUserCameraModalOpen, showToast]);

  const openUserImageSourceMenu = useCallback((slotIndex?: number, isNewSlot?: boolean) => {
    if (userCardImageCount >= 40) return;
    if (slotIndex !== undefined && !isNewSlot) {
      // 기존 이미지가 있는 슬롯 교체
      setReplacingSlotIndex(slotIndex);
      setAddingSlotIndex(null);
    } else if (slotIndex !== undefined && isNewSlot) {
      // 빈 슬롯에 이미지 추가
      setReplacingSlotIndex(null);
      setAddingSlotIndex(slotIndex);
    } else {
      // 일반 추가 (첫 빈 슬롯에)
      setReplacingSlotIndex(null);
      setAddingSlotIndex(null);
    }
    setIsUserImageSourceMenuOpen(true);
  }, [userCardImageCount]);

  const calculateInitialCropAndZoom = useCallback(async (imageSrc: string) => {
    const containerSize = 288; // 이미지 표시 영역(컨테이너) 크기
    const cropSize = 144; // react-easy-crop의 cropSize prop 값
    try {
      const img = await createHtmlImage(imageSrc, false);
      const imgWidth = img.width;
      const imgHeight = img.height;
      
      // 이미지의 가로/세로 중 더 작은 쪽을 찾음
      const minImageSize = Math.min(imgWidth, imgHeight);
      
      // 원본 크기로 표시되도록 zoom 설정
      // zoom = 1로 설정하면 이미지가 원본 크기 그대로 표시됨
      const initialZoom = 1;
      
      // react-easy-crop에서 실제 표시되는 이미지 크기 계산
      // react-easy-crop는 cropSize(144px) 기준으로 이미지를 표시하고, 이것이 containerSize(288px)로 확대되어 보임
      // 따라서: 컨테이너에 표시되는 실제 크기 = (원본 이미지 크기 * zoom) / cropSize * containerSize
      // 예: 144px * 4 / 144 * 288 = 576 / 144 * 288 = 4 * 288 = 1152... 아니다
      
      // 다시 생각해보면:
      // react-easy-crop에서 zoom된 이미지의 작은 쪽 크기가 cropSize 영역 안에서 보임
      // 이 cropSize 영역이 containerSize로 표시되므로:
      // 컨테이너에 표시되는 크기 = (원본 이미지 작은 쪽 * zoom) / cropSize * containerSize
      // = (144 * 4) / 144 * 288 = 576 / 144 * 288 = 4 * 288 = 1152... 이것도 아님
      
      // 실제로는: zoom된 이미지가 cropSize 기준으로 표시되고, 이것이 containerSize로 확대되어 보임
      // 표시 비율 = containerSize / cropSize = 288 / 144 = 2
      // 따라서: 컨테이너에 표시되는 크기 = (원본 이미지 크기 * zoom) * (containerSize / cropSize)
      // 실제 표시될 이미지 크기 계산 (검증용)
      // react-easy-crop에서 실제 표시되는 크기 계산:
      // 1. cropSize 영역 안에서의 크기 = 원본 이미지 크기 * zoom
      // 2. 이것이 containerSize로 확대되어 표시됨
      // 따라서: 컨테이너에 표시되는 크기 = (원본 이미지 크기 * zoom) * (containerSize / cropSize)
      const scaleRatio = containerSize / cropSize; // 288 / 144 = 2
      const displayedScaledWidth = (imgWidth * initialZoom) * scaleRatio;
      const displayedScaledHeight = (imgHeight * initialZoom) * scaleRatio;
      const displayedScaledMinSize = Math.min(displayedScaledWidth, displayedScaledHeight);
      const displayedScaledMaxSize = Math.max(displayedScaledWidth, displayedScaledHeight);
      
      console.log('🖼️ 이미지 크기 계산:', {
        '1️⃣ 원본 이미지': {
          가로: `${imgWidth}px`,
          세로: `${imgHeight}px`,
          작은_쪽: `${minImageSize}px`,
        },
        '2️⃣ 컨테이너 및 크롭 영역': {
          컨테이너_크기: `${containerSize}px (이미지 표시 영역)`,
          크롭_영역_크기: `${cropSize}px (react-easy-crop 기준)`,
          확대_비율: `${containerSize}/${cropSize} = ${scaleRatio}`,
          설명: 'cropSize 영역이 containerSize로 확대되어 표시됨',
        },
        '3️⃣ Zoom 계산 과정': {
          목표: `원본 크기 그대로 표시`,
          설명: 'zoom = 1로 설정하여 이미지를 원본 크기로 표시',
          계산식: `zoom = 1`,
          최종_zoom: initialZoom.toFixed(4),
          결과: `크롭퍼에 표시되는 크기: 가로 ${imgWidth}px, 세로 ${imgHeight}px (원본 크기)`,
        },
        '4️⃣ 컨테이너에 표시될 이미지 크기': {
          가로: `${displayedScaledWidth.toFixed(2)}px`,
          세로: `${displayedScaledHeight.toFixed(2)}px`,
          작은_쪽: `${displayedScaledMinSize.toFixed(2)}px`,
          큰_쪽: `${displayedScaledMaxSize.toFixed(2)}px`,
          계산식: `(원본 * zoom) * (containerSize / cropSize)`,
          상세_가로: `(${imgWidth} * ${initialZoom.toFixed(4)}) * ${scaleRatio} = ${displayedScaledWidth.toFixed(2)}px`,
          상세_세로: `(${imgHeight} * ${initialZoom.toFixed(4)}) * ${scaleRatio} = ${displayedScaledHeight.toFixed(2)}px`,
          크롭퍼_표시_크기_가로: `${(imgWidth * initialZoom).toFixed(2)}px (144px 영역 안)`,
          크롭퍼_표시_크기_세로: `${(imgHeight * initialZoom).toFixed(2)}px (144px 영역 안)`,
        },
        '5️⃣ 목표 달성 확인': {
          목표: `작은 쪽이 ${containerSize}px`,
          실제: `작은 쪽 ${displayedScaledMinSize.toFixed(2)}px`,
          차이: `${Math.abs(displayedScaledMinSize - containerSize).toFixed(2)}px`,
          달성여부: Math.abs(displayedScaledMinSize - containerSize) < 0.01 ? '✅ 달성' : '❌ 미달성',
        }
      });
      
      // 중앙에 위치하도록 crop 계산
      const initialCrop = { x: 0, y: 0 };
      
      return { zoom: initialZoom, crop: initialCrop };
    } catch (error) {
      console.error('이미지 크기 계산 실패:', error);
      return { zoom: 1, crop: { x: 0, y: 0 } };
    }
  }, []);

  const handleSelectUserImageFile = useCallback(async (file: File) => {
    try {
      const src = await readFileAsDataURL(file);
      setRawUserImageSrc(src);
      userCropPixelsRef.current = null;
      setIsUserCropModalOpen(true);
      setIsUserImageSourceMenuOpen(false);
      
      // 이미지가 로드되면 초기 zoom과 crop 계산
      const { zoom: initialZoom, crop: initialCrop } = await calculateInitialCropAndZoom(src);
      setZoom(initialZoom);
      setCrop(initialCrop);
    } catch (e) {
      console.error('파일 읽기 실패:', e);
      showToast('이미지 파일을 읽지 못했습니다.');
    }
  }, [showToast, calculateInitialCropAndZoom]);

  const handleStartUserFileUpload = useCallback(() => {
    setIsUserImageSourceMenuOpen(false);
    userFileInputRef.current?.click();
  }, []);

  const handleStartUserCamera = useCallback(() => {
    setIsUserImageSourceMenuOpen(false);
    setIsUserCameraModalOpen(true);
  }, []);

  const handleCaptureUserFromCamera = useCallback(async () => {
    const video = userVideoRef.current;
    if (!video) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    setIsUserCameraModalOpen(false);
    setRawUserImageSrc(dataUrl);
    userCropPixelsRef.current = null;
    setIsUserCropModalOpen(true);
    
    // 이미지가 로드되면 초기 zoom과 crop 계산
    const { zoom: initialZoom, crop: initialCrop } = await calculateInitialCropAndZoom(dataUrl);
    setZoom(initialZoom);
    setCrop(initialCrop);
  }, [calculateInitialCropAndZoom]);

  const handleUploadCroppedUserCardImage = useCallback(async () => {
    if (!rawUserImageSrc) return;
    const cropPixels = userCropPixelsRef.current;
    if (!cropPixels) {
      showToast('크롭 영역을 결정해 주세요.');
      return;
    }
    if (!userId) {
      showToast('사용자 정보를 찾을 수 없습니다.');
      return;
    }
    // replacingSlotIndex가 있으면 해당 슬롯에 저장
    // addingSlotIndex가 있으면 해당 빈 슬롯에 저장
    // 둘 다 없으면 첫 빈 슬롯 찾기
    const targetSlotIndex = replacingSlotIndex !== null 
      ? replacingSlotIndex 
      : addingSlotIndex !== null
      ? addingSlotIndex
      : userCardImagesBySlot.findIndex((v) => !v);
    
    if (targetSlotIndex < 0) {
      showToast('사용자 이미지는 최대 40개까지 등록할 수 있습니다.');
      return;
    }

    setIsUploadingUserCardImage(true);
    try {
      // 가로 세로 144px 정사각형으로 최적화
      const blob = await cropToMaxWidthWebp(rawUserImageSrc, cropPixels, 144, {
        quality: 0.8,
        isSquare: true,
        useCors: !rawUserImageSrc.startsWith('data:')
      });
      
      // 기존 이미지가 있으면 삭제
      const existingUrl = userCardImagesBySlot[targetSlotIndex];
      if (existingUrl && replacingSlotIndex !== null) {
        try {
          // URL에서 파일 경로 추출
          const urlObj = new URL(existingUrl);
          // URL 인코딩된 경로 디코딩 (%2F -> /)
          const decodedPathname = decodeURIComponent(urlObj.pathname);
          // /o/ 다음의 경로를 추출 (예: /o/Users%2FuserId%2FclassId%2FCardImage%2Ffilename.jpg)
          const pathMatch = decodedPathname.match(/\/o\/(.+)$/);
          if (pathMatch) {
            const filePath = pathMatch[1]; // 이미 디코딩된 경로
            const fileRef = storageRef(storage, filePath);
            await deleteObject(fileRef);
          }
        } catch (deleteError) {
          console.error('기존 이미지 삭제 실패:', deleteError);
          // 삭제 실패해도 계속 진행
        }
      }
      
      // 타임스탬프 기반 파일명으로 저장
      const timestamp = Date.now();
      const path = buildUserStoragePath(userId, classId, 'CardImage', `${timestamp}.${IMAGE_WEBP_EXT}`);
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, blob, { contentType: IMAGE_WEBP_MIME });
      const url = await getDownloadURL(fileRef);

      // 해당 슬롯에 저장
      const newOrder = [...userCardImagesBySlot];
      newOrder[targetSlotIndex] = url;
      setUserCardImagesBySlot(newOrder);

      // 서버에 순서 저장 (null 포함한 전체 배열)
      if (itemCardService) {
        try {
          await itemCardService.saveUserCardImageOrder(newOrder);
        } catch (error) {
          console.error('❌ 이미지 순서 저장 실패:', error);
        }
      }

      // 업로드 직후 선택 상태 반영
      if (editingItem) {
        setEditingItem({ ...editingItem, cardImage: url });
      }

      setIsUserCropModalOpen(false);
      setReplacingSlotIndex(null);
      setAddingSlotIndex(null);
      showToast(replacingSlotIndex !== null ? '이미지가 교체되었습니다.' : '이미지가 등록되었습니다.');
    } catch (e) {
      console.error('사용자 카드 이미지 업로드 실패:', e);
      showToast('이미지 업로드에 실패했습니다.');
    } finally {
      setIsUploadingUserCardImage(false);
    }
  }, [editingItem, rawUserImageSrc, showToast, userCardImagesBySlot, userId, classId, replacingSlotIndex, addingSlotIndex, itemCardService]);

  // 이미지가 사용 중인지 확인하는 함수
  const isImageInUse = useCallback((imageUrl: string): boolean => {
    const allItems = [...levelupItems, ...assignmentItems, ...storeItems];
    return allItems.some(item => item.cardImage === imageUrl);
  }, [levelupItems, assignmentItems, storeItems]);

  // 이미지 삭제 함수
  const handleDeleteUserImage = useCallback(async (imageUrl: string, slotIndex: number) => {
    if (!userId) {
      showToast('사용자 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      // URL에서 파일 경로 추출
      const urlObj = new URL(imageUrl);
      // URL 인코딩된 경로 디코딩 (%2F -> /)
      const decodedPathname = decodeURIComponent(urlObj.pathname);
      // /o/ 다음의 경로를 추출 (예: /o/Users%2FuserId%2FclassId%2FCardImage%2Ffilename.jpg)
      const pathMatch = decodedPathname.match(/\/o\/(.+)$/);
      
      if (pathMatch) {
        const filePath = pathMatch[1]; // 이미 디코딩된 경로
        const fileRef = storageRef(storage, filePath);
        await deleteObject(fileRef);
        console.log('✅ 이미지 파일 삭제 완료:', filePath);
      } else {
        console.error('파일 경로를 추출할 수 없습니다:', imageUrl, 'decodedPathname:', decodedPathname);
        throw new Error('파일 경로를 추출할 수 없습니다.');
      }

      // 슬롯에서 제거
      const newOrder = [...userCardImagesBySlot];
      newOrder[slotIndex] = null;
      setUserCardImagesBySlot(newOrder);

      // 서버에 순서 저장 (null 포함한 전체 배열)
      if (itemCardService) {
        try {
          await itemCardService.saveUserCardImageOrder(newOrder);
        } catch (error) {
          console.error('❌ 이미지 순서 저장 실패:', error);
        }
      }

      // 현재 편집 중인 아이템의 이미지가 삭제된 경우 기본 이미지로 변경
      if (editingItem && editingItem.cardImage === imageUrl) {
        setEditingItem({ ...editingItem, cardImage: 'default' });
      }

      showToast('이미지가 삭제되었습니다.');
    } catch (error) {
      console.error('이미지 삭제 실패:', error);
      showToast('이미지 삭제에 실패했습니다.');
    }
  }, [userId, showToast, editingItem, userCardImagesBySlot, itemCardService]);

  // 이미지 대체 시작 (수정 아이콘 클릭)
  const handleReplaceUserImage = useCallback((slotIndex: number) => {
    setReplacingSlotIndex(slotIndex);
    setIsUserImageSourceMenuOpen(true);
  }, []);

  // 서비스 초기화
  useEffect(() => {
    if (userId && classId) {
      setItemCardService(new ItemCardService(userId, classId));
    }
  }, [userId, classId]);
  
  // 프로필 카드 가격 로드 (세트별)
  useEffect(() => {
    const loadProfileCardCost = async () => {
      if (!userId || !classId || !isOpen) return;
      try {
        const defaultSet = await defaultSetService.loadDefaultSet(userId, classId);
        if (defaultSet?.profileCardCostB !== undefined) {
          setProfileCardCostInputB(defaultSet.profileCardCostB.toString());
          setInitialProfileCardCostB(defaultSet.profileCardCostB);
        }
        if (defaultSet?.profileCardCostC !== undefined) {
          setProfileCardCostInputC(defaultSet.profileCardCostC.toString());
          setInitialProfileCardCostC(defaultSet.profileCardCostC);
        }
        // 기존 단일 가격이 있으면 둘 다에 적용 (마이그레이션)
        if (defaultSet?.profileCardCost !== undefined && 
            defaultSet?.profileCardCostB === undefined && 
            defaultSet?.profileCardCostC === undefined) {
          setProfileCardCostInputB(defaultSet.profileCardCost.toString());
          setProfileCardCostInputC(defaultSet.profileCardCost.toString());
          setInitialProfileCardCostB(defaultSet.profileCardCost);
          setInitialProfileCardCostC(defaultSet.profileCardCost);
        }
      } catch (error) {
        console.error('프로필 카드 가격 로드 실패:', error);
      }
    };
    loadProfileCardCost();
  }, [userId, classId, isOpen]);

  // 아이템 카드 지급 여부 확인
  const checkGrantedItemCards = useCallback(async () => {
    if (!itemCardService) return;
    
    setIsCheckingGranted(true);
    try {
      const allItems = [...levelupItems, ...assignmentItems, ...storeItems];
      const grantedSet = new Set<string>();
      
      // 각 아이템 카드의 지급 여부 확인
      for (const item of allItems) {
        const isGranted = await itemCardService.isItemCardGrantedToStudents(item.itemCardId);
        if (isGranted) {
          grantedSet.add(item.itemCardId);
        }
      }
      
      setGrantedItemCards(grantedSet);
    } catch (error) {
      console.error('아이템 카드 지급 여부 확인 실패:', error);
    } finally {
      setIsCheckingGranted(false);
    }
  }, [itemCardService, levelupItems, assignmentItems, storeItems]);

  // 데이터 로드
  const loadData = useCallback(async () => {
    if (!itemCardService) return;

    setIsLoading(true);
    try {
      const data = await itemCardService.loadItemCardSet();
      if (data) {
        setLevelupItems(data.levelupItem);
        setAssignmentItems(data.assignmentItem);
        setStoreItems(data.storeItem);
        
        // 초기 데이터 저장 (변경사항 확인용)
        setInitialLevelupItems(JSON.parse(JSON.stringify(data.levelupItem)));
        setInitialAssignmentItems(JSON.parse(JSON.stringify(data.assignmentItem)));
        setInitialStoreItems(JSON.parse(JSON.stringify(data.storeItem)));
      }
    } catch (error) {
      console.error('아이템 카드 데이터 로드 실패:', error);
    } finally {
      setIsLoading(false);
    }
  }, [itemCardService]);

  // 초기 데이터 로드
  useEffect(() => {
    if (isOpen && itemCardService) {
      loadData();
    }
  }, [isOpen, itemCardService, loadData]);

  // 아이템 카드 목록이 변경되면 지급 여부 확인
  useEffect(() => {
    if (isOpen && itemCardService && (levelupItems.length > 0 || assignmentItems.length > 0 || storeItems.length > 0)) {
      void checkGrantedItemCards();
    }
  }, [isOpen, itemCardService, checkGrantedItemCards, levelupItems.length, assignmentItems.length, storeItems.length]);

  // 모달 열릴 때 배경 스크롤 비활성화 및 베이직 플랜 자동 탭 선택
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      // 베이직 플랜에서는 자동으로 프로필 카드 탭 선택
      if (user.userType === 'basic') {
        setActiveTab('profileCard');
      }

      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen, user.userType]);

  // 다른 클래스 데이터 가져오기 모달 열기
  const handleOpenImportModal = async () => {
    setShowImportModal(true);
    setSelectedSourceClassId('');
    setSourceLevelupItems([]);
    setSourceAssignmentItems([]);
    setSourceStoreItems([]);
    setSelectedItemCardIds(new Set());
    
    try {
      const allClasses = await userDataService.loadUserClasses(userId);
      const filteredClasses = allClasses.filter(c => c.id !== classId);
      setAvailableClasses(filteredClasses);
    } catch (error) {
      console.error('클래스 목록 로드 실패:', error);
      setAvailableClasses([]);
    }
  };

  // 다른 클래스 데이터 가져오기 모달 닫기
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setSelectedSourceClassId('');
    setSourceLevelupItems([]);
    setSourceAssignmentItems([]);
    setSourceStoreItems([]);
    setSelectedItemCardIds(new Set());
    setIsClassDropdownOpen(false);
  };

  // 소스 클래스 변경 핸들러
  const handleSourceClassChange = async (sourceClassId: string) => {
    setSelectedSourceClassId(sourceClassId);
    setIsClassDropdownOpen(false);
    setIsLoadingSourceItems(true);
    
    try {
      const sourceService = new ItemCardService(userId, sourceClassId);
      const sourceData = await sourceService.loadItemCardSet();
      
      if (sourceData) {
        setSourceLevelupItems(sourceData.levelupItem || []);
        setSourceAssignmentItems(sourceData.assignmentItem || []);
        setSourceStoreItems(sourceData.storeItem || []);
      } else {
        setSourceLevelupItems([]);
        setSourceAssignmentItems([]);
        setSourceStoreItems([]);
      }
      setSelectedItemCardIds(new Set());
    } catch (error) {
      console.error('소스 클래스 아이템 카드 로드 실패:', error);
      setSourceLevelupItems([]);
      setSourceAssignmentItems([]);
      setSourceStoreItems([]);
    } finally {
      setIsLoadingSourceItems(false);
    }
  };

  // 아이템 카드 선택 토글
  const handleToggleItemSelection = (itemCardId: string) => {
    setSelectedItemCardIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemCardId)) {
        newSet.delete(itemCardId);
      } else {
        newSet.add(itemCardId);
      }
      return newSet;
    });
  };

  // 선택한 아이템 카드 적용
  const handleApplySelectedItems = async () => {
    if (!itemCardService || selectedItemCardIds.size === 0) return;
    
    try {
      // 모든 카테고리에서 선택한 아이템 수집
      const allSelectedItems: { item: ItemCard; category: ItemCardCategory }[] = [];
      
      sourceLevelupItems.forEach(item => {
        if (selectedItemCardIds.has(item.itemCardId)) {
          allSelectedItems.push({ item, category: 'levelup' });
        }
      });
      
      sourceAssignmentItems.forEach(item => {
        if (selectedItemCardIds.has(item.itemCardId)) {
          allSelectedItems.push({ item, category: 'assignment' });
        }
      });
      
      sourceStoreItems.forEach(item => {
        if (selectedItemCardIds.has(item.itemCardId)) {
          allSelectedItems.push({ item, category: 'store' });
        }
      });
      
      if (allSelectedItems.length === 0) {
        alert('선택한 아이템이 없습니다.');
        return;
      }
      
      // 카테고리별로 분류
      const itemsByCategory: { [key in ItemCardCategory]: ItemCard[] } = {
        levelup: [],
        assignment: [],
        store: []
      };
      
      allSelectedItems.forEach(({ item, category }) => {
        // 현재 카테고리의 아이템 목록 가져오기
        const currentItems = 
          category === 'levelup' ? levelupItems :
          category === 'assignment' ? assignmentItems :
          storeItems;
        
        // 중복 체크 (이름으로)
        const existingNames = new Set(currentItems.map(i => i.name.toLowerCase()));
        if (!existingNames.has(item.name.toLowerCase())) {
          // 새 ID 생성하여 추가
          const newItem = {
            ...item,
            itemCardId: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
          };
          itemsByCategory[category].push(newItem);
        }
      });
      
      // 상태 업데이트
      if (itemsByCategory.levelup.length > 0) {
        setLevelupItems(prev => [...prev, ...itemsByCategory.levelup]);
      }
      if (itemsByCategory.assignment.length > 0) {
        setAssignmentItems(prev => [...prev, ...itemsByCategory.assignment]);
      }
      if (itemsByCategory.store.length > 0) {
        setStoreItems(prev => [...prev, ...itemsByCategory.store]);
      }
      
      const totalAdded = itemsByCategory.levelup.length + itemsByCategory.assignment.length + itemsByCategory.store.length;
      
      if (totalAdded === 0) {
        alert('모든 선택한 아이템이 이미 존재합니다.');
        return;
      }
      
      handleCloseImportModal();
      showToast(`${totalAdded}개의 아이템이 추가되었습니다.`);
    } catch (error) {
      console.error('아이템 카드 적용 실패:', error);
      alert('아이템 카드 적용에 실패했습니다.');
    }
  };

  // 새 아이템 카드 추가
  const handleAddItemCard = () => {
    // 프로필 카드 탭에서는 아이템 추가 불가
    if (activeTab === 'profileCard') return;
    
    // 프리 플랜에서는 카드 생성 불가
    if (user.userType === 'free') {
      setLicenseLimitMessage('프리 플랜에서는 아이템 카드를 생성할 수 없습니다. 이용권을 업그레이드하세요.');
      setShowLicenseLimitModal(true);
      return;
    }
    
    const newItem: ItemCard = {
      itemCardId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: '새 아이템',
      memo: '아이템 설명',
      type: 'passive' as ItemCardType,
      cost: (activeTab === 'levelup' || activeTab === 'assignment') ? 'Nocharge' as const : 0,
      category: activeTab as ItemCardCategory,
      cardImage: 'default',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // 로컬 상태에만 추가 (서버 저장은 저장 버튼을 눌렀을 때)
    if (activeTab === 'levelup') {
      setLevelupItems(prev => [...prev, newItem]);
    } else if (activeTab === 'assignment') {
      setAssignmentItems(prev => [...prev, newItem]);
    } else if (activeTab === 'store') {
      setStoreItems(prev => [...prev, newItem]);
    }

    // 생성된 아이템을 수정 모드로 설정
    setEditingItem(newItem);
    setIsNewItem(true);
  };

  // 아이템 카드 수정
  const handleEditItemCard = (item: ItemCard) => {
    // 원본 아이템의 복사본을 만들어서 수정 (원본 배열은 변경하지 않음)
    const itemCopy = JSON.parse(JSON.stringify(item));
    setEditingItem(itemCopy);
    setIsNewItem(false);
  };

  // 아이템 카드 저장 (로컬에만 반영, 서버 저장은 [저장] 버튼 클릭 시)
  const handleSaveItemCard = (item: ItemCard) => {
    if (activeTab === 'levelup') {
      setLevelupItems(prev => prev.map(p => p.itemCardId === item.itemCardId ? item : p));
    } else if (activeTab === 'assignment') {
      setAssignmentItems(prev => prev.map(p => p.itemCardId === item.itemCardId ? item : p));
    } else {
      setStoreItems(prev => prev.map(p => p.itemCardId === item.itemCardId ? item : p));
    }
    setEditingItem(null);
    setIsNewItem(false);
  };

  // 아이템 카드 삭제 확인
  const handleDeleteItemCard = (itemCardId: string, category: ItemCardCategory) => {
    // 지급된 아이템 카드는 삭제 불가
    if (grantedItemCards.has(itemCardId)) {
      showToast('학생들에게 지급된 아이템 카드는 삭제할 수 없습니다.');
      return;
    }
    
    setItemToDelete({ id: itemCardId, category });
    setIsDeleteConfirmOpen(true);
  };

  // 아이템 카드 삭제 실행 (로컬에만 반영)
  const executeDeleteItemCard = () => {
    if (!itemToDelete) return;

    if (itemToDelete.category === 'levelup') {
      setLevelupItems(prev => prev.filter(p => p.itemCardId !== itemToDelete.id));
    } else if (itemToDelete.category === 'assignment') {
      setAssignmentItems(prev => prev.filter(p => p.itemCardId !== itemToDelete.id));
    } else {
      setStoreItems(prev => prev.filter(p => p.itemCardId !== itemToDelete.id));
    }

    setIsDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  // 수정 취소
  const handleCancelEdit = () => {
    if (isNewItem && editingItem) {
      // 새 아이템인 경우 로컬 상태에서 제거
      if (activeTab === 'levelup') {
        setLevelupItems(prev => prev.filter(p => p.itemCardId !== editingItem.itemCardId));
      } else if (activeTab === 'assignment') {
        setAssignmentItems(prev => prev.filter(p => p.itemCardId !== editingItem.itemCardId));
      } else {
        setStoreItems(prev => prev.filter(p => p.itemCardId !== editingItem.itemCardId));
      }
      setIsNewItem(false);
    } else if (editingItem && !isNewItem) {
      // 기존 아이템 수정 취소: 초기 데이터에서 원본 아이템으로 복원
      const initialItems = activeTab === 'levelup' ? initialLevelupItems : 
                          activeTab === 'assignment' ? initialAssignmentItems : 
                          initialStoreItems;
      const originalItem = initialItems.find(item => item.itemCardId === editingItem.itemCardId);
      if (originalItem) {
        // 초기 아이템의 복사본으로 복원
        const restoredItem = JSON.parse(JSON.stringify(originalItem));
        if (activeTab === 'levelup') {
          setLevelupItems(prev => prev.map(p => p.itemCardId === editingItem.itemCardId ? restoredItem : p));
        } else if (activeTab === 'assignment') {
          setAssignmentItems(prev => prev.map(p => p.itemCardId === editingItem.itemCardId ? restoredItem : p));
        } else {
          setStoreItems(prev => prev.map(p => p.itemCardId === editingItem.itemCardId ? restoredItem : p));
        }
      }
    }
    setEditingItem(null);
  };

  // 변경사항이 있는지 확인하는 함수
  const hasUnsavedChanges = useCallback(() => {
    // 아이템 카드 변경사항 확인
    const levelupChanged = JSON.stringify(levelupItems) !== JSON.stringify(initialLevelupItems);
    const assignmentChanged = JSON.stringify(assignmentItems) !== JSON.stringify(initialAssignmentItems);
    const storeChanged = JSON.stringify(storeItems) !== JSON.stringify(initialStoreItems);
    
    // 프로필 카드 가격 변경사항 확인 (입력값 기준)
    const costB = parseInt(profileCardCostInputB, 10);
    const costC = parseInt(profileCardCostInputC, 10);
    const profileCostBChanged = !isNaN(costB) && costB !== initialProfileCardCostB;
    const profileCostCChanged = !isNaN(costC) && costC !== initialProfileCardCostC;
    
    return levelupChanged || assignmentChanged || storeChanged || profileCostBChanged || profileCostCChanged;
  }, [levelupItems, assignmentItems, storeItems, initialLevelupItems, initialAssignmentItems, initialStoreItems, profileCardCostInputB, profileCardCostInputC, initialProfileCardCostB, initialProfileCardCostC]);
  
  // 모달 닫기 핸들러 (변경사항 확인)
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setIsCloseConfirmModalOpen(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);
  
  // 확인 모달에서 닫기 확인
  const handleConfirmClose = useCallback(() => {
    setIsCloseConfirmModalOpen(false);
    onClose();
  }, [onClose]);
  
  // 확인 모달에서 취소
  const handleCancelClose = useCallback(() => {
    setIsCloseConfirmModalOpen(false);
  }, []);

  // 전체 저장 (서버 반영)
  const handleSaveAll = async () => {
    if (!itemCardService) return;

    setIsSaving(true);
    try {
      // 1. 프로필 카드 가격 저장 (변경된 경우)
      const newCostB = parseInt(profileCardCostInputB) || 0;
      const newCostC = parseInt(profileCardCostInputC) || 0;
      if (newCostB >= 0 && newCostC >= 0 && (newCostB !== initialProfileCardCostB || newCostC !== initialProfileCardCostC)) {
      await defaultSetService.updateDefaultSet(userId, classId, {
        profileCardCostB: newCostB,
        profileCardCostC: newCostC
      });
      setProfileCardCostInputB(newCostB.toString());
      setProfileCardCostInputC(newCostC.toString());
        setInitialProfileCardCostB(newCostB);
        setInitialProfileCardCostC(newCostC);
      }

      // 2. 아이템 카드 세트 전체 저장
      await itemCardService.saveItemCardSet(levelupItems, assignmentItems, storeItems);

      // 3. 서버에서 최종 데이터 로드
      const fresh = await itemCardService.loadItemCardSet();
      if (fresh) {
        setLevelupItems(fresh.levelupItem || []);
        setAssignmentItems(fresh.assignmentItem || []);
        setStoreItems(fresh.storeItem || []);
        setInitialLevelupItems(JSON.parse(JSON.stringify(fresh.levelupItem || [])));
        setInitialAssignmentItems(JSON.parse(JSON.stringify(fresh.assignmentItem || [])));
        setInitialStoreItems(JSON.parse(JSON.stringify(fresh.storeItem || [])));
      }
      setEditingItem(null);

      showToast('저장되었습니다.');
    } catch (error) {
      console.error('저장 실패:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const currentItems = activeTab === 'levelup' ? levelupItems : 
                      activeTab === 'assignment' ? assignmentItems : 
                      activeTab === 'store' ? storeItems : [];

  return (
    <>
      <ModalOverlay onClick={handleClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <h3>아이템 카드 설정</h3>
            <CloseButton onClick={handleClose}>×</CloseButton>
          </ModalHeader>

          <TabContainer>
            <TabButton
              $isActive={activeTab === 'levelup'}
              $isDisabled={user.userType === 'free' || user.userType === 'basic'}
              onClick={() => {
                if (user.userType === 'free' || user.userType === 'basic') {
                  setLicenseLimitMessage('프리/베이직 플랜에서는 레벨업 아이템을 사용할 수 없습니다. 이용권을 업그레이드하세요.');
                  setShowLicenseLimitModal(true);
                  return;
                }
                setActiveTab('levelup');
              }}
            >
              레벨업 아이템
            </TabButton>
            <TabButton
              $isActive={activeTab === 'assignment'}
              $isDisabled={user.userType === 'free' || user.userType === 'basic'}
              onClick={() => {
                if (user.userType === 'free' || user.userType === 'basic') {
                  setLicenseLimitMessage('프리/베이직 플랜에서는 과제보상 아이템을 사용할 수 없습니다. 이용권을 업그레이드하세요.');
                  setShowLicenseLimitModal(true);
                  return;
                }
                setActiveTab('assignment');
              }}
            >
              과제보상 아이템
            </TabButton>
            <TabButton
              $isActive={activeTab === 'store'}
              $isDisabled={user.userType === 'free' || user.userType === 'basic'}
              onClick={() => {
                if (user.userType === 'free' || user.userType === 'basic') {
                  setLicenseLimitMessage('프리/베이직 플랜에서는 스토어 아이템을 사용할 수 없습니다. 이용권을 업그레이드하세요.');
                  setShowLicenseLimitModal(true);
                  return;
                }
                setActiveTab('store');
              }}
            >
              스토어 아이템
            </TabButton>
            <TabButton
              $isActive={activeTab === 'profileCard'}
              $isDisabled={user.userType === 'free'}
              onClick={() => {
                if (user.userType === 'free') {
                  setLicenseLimitMessage('프리 플랜에서는 프로필 카드를 사용할 수 없습니다. 이용권을 업그레이드하세요.');
                  setShowLicenseLimitModal(true);
                  return;
                }
                setActiveTab('profileCard');
              }}
            >
              프로필 카드
            </TabButton>
          </TabContainer>

          <ContentArea>
            {activeTab === 'profileCard' ? (
              <ProfileCardTabContent>
                <ProfileCardSetsRow>
                  {/* 동물 */}
                  <ProfileCardSetItem>
                    <ProfileCardSetTitle>동물</ProfileCardSetTitle>
                    <ProfileCardStackWrapper>
                      <ProfileCardStack>
                        <ProfileCardBack $index={2} />
                        <ProfileCardBack $index={1} />
                        <ProfileCardFront>
                          <ProfileCardQuestion>?</ProfileCardQuestion>
                        </ProfileCardFront>
                      </ProfileCardStack>
                    </ProfileCardStackWrapper>
                    <ProfileCardCostInputRow>
                      <ProfileCardCoinIcon>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                          <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                          <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                        </svg>
                      </ProfileCardCoinIcon>
                      <ProfileCardCostInput
                        type="number"
                        value={profileCardCostInputB}
                        onChange={(e) => setProfileCardCostInputB(e.target.value)}
                        min="0"
                      />
                      <span style={{ color: '#6c757d', fontSize: '14px' }}>코인</span>
                    </ProfileCardCostInputRow>
                  </ProfileCardSetItem>
                  
                  {/* 캐릭터 */}
                  <ProfileCardSetItem>
                    <ProfileCardSetTitle>캐릭터</ProfileCardSetTitle>
                    <ProfileCardStackWrapper>
                      <ProfileCardStack>
                        <ProfileCardBack $index={2} />
                        <ProfileCardBack $index={1} />
                        <ProfileCardFront>
                          <ProfileCardQuestion>?</ProfileCardQuestion>
                        </ProfileCardFront>
                      </ProfileCardStack>
                    </ProfileCardStackWrapper>
                    <ProfileCardCostInputRow>
                      <ProfileCardCoinIcon>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                          <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                          <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                        </svg>
                      </ProfileCardCoinIcon>
                      <ProfileCardCostInput
                        type="number"
                        value={profileCardCostInputC}
                        onChange={(e) => setProfileCardCostInputC(e.target.value)}
                        min="0"
                      />
                      <span style={{ color: '#6c757d', fontSize: '14px' }}>코인</span>
                    </ProfileCardCostInputRow>
                  </ProfileCardSetItem>
                </ProfileCardSetsRow>
                
                <ProfileCardCostDescription>
                  멤버가 프로필 카드를 구입할 때 세트별로 차감되는 코인입니다.
                </ProfileCardCostDescription>
              </ProfileCardTabContent>
            ) : isLoading ? (
              <LoadingText>로딩 중...</LoadingText>
            ) : (
              <ItemCardGrid>
                {currentItems.map((item) => (
                  <ItemCardBlock
                    key={item.itemCardId}
                    $isEditing={editingItem?.itemCardId === item.itemCardId}
                    onClick={() => handleEditItemCard(item)}
                  >
                    {editingItem?.itemCardId === item.itemCardId ? (
                      <EditForm onClick={(e) => e.stopPropagation()}>
                        {/* 카드 이미지 버튼 */}
                        <CardImageButtonWrapper>
                          <CardImageButton 
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsCardImageModalOpen(true);
                            }}
                            title="카드 이미지 선택"
                          >
                            {editingItem.cardImage && editingItem.cardImage !== 'default' ? (
                              isNumericCardImage(editingItem.cardImage) ? (
                                <CardImagePreviewImg src={getIconBasicImagePath(editingItem.cardImage)} alt="카드 이미지" />
                              ) : (
                                <CardImagePreviewImg src={editingItem.cardImage} alt="카드 이미지" />
                              )
                            ) : (
                              <CardImagePlaceholder>?</CardImagePlaceholder>
                            )}
                          </CardImageButton>
                        </CardImageButtonWrapper>

                        {/* 타입 토글 버튼 */}
                        <TypeToggleWrapper>
                          <TypeToggleButton 
                            $isActive={editingItem.type === 'active'}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingItem({
                                ...editingItem,
                                type: editingItem.type === 'passive' ? 'active' : 'passive',
                                useCount: editingItem.type === 'passive' ? 1 : undefined
                              });
                            }}
                          >
                            <TypeToggleOption $isActive={editingItem.type === 'passive'}>패시브</TypeToggleOption>
                            <TypeToggleOption $isActive={editingItem.type === 'active'}>액티브</TypeToggleOption>
                          </TypeToggleButton>
                        </TypeToggleWrapper>

                        <InputGroup>
                          <Label>아이템 이름</Label>
                          <Input
                            type="text"
                            value={editingItem.name}
                            onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                            onFocus={(e) => {
                              if (e.target.value === '새 아이템') {
                                e.target.select();
                              }
                            }}
                          />
                        </InputGroup>

                        {editingItem.type === 'active' && (
                          <InputGroup>
                            <Label>사용 횟수</Label>
                            <Input
                              type="number"
                              min={1}
                              value={editingItem.useCount == null ? '' : editingItem.useCount}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  setEditingItem({
                                    ...editingItem,
                                    useCount: undefined
                                  });
                                } else {
                                  const numValue = parseInt(value, 10);
                                  if (!isNaN(numValue) && numValue >= 1) {
                                    setEditingItem({
                                      ...editingItem,
                                      useCount: numValue
                                    });
                                  }
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value === '' || parseInt(value, 10) < 1) {
                                  setEditingItem({
                                    ...editingItem,
                                    useCount: 1
                                  });
                                }
                              }}
                              onFocus={(e) => {
                                e.target.select();
                              }}
                            />
                          </InputGroup>
                        )}

                        {activeTab === 'store' && (
                          <InputGroup>
                            <Label>비용</Label>
                            <Input
                              type="number"
                              min="0"
                              value={editingItem.cost === 'Nocharge' ? 0 : editingItem.cost}
                              onChange={(e) => setEditingItem({
                                ...editingItem,
                                cost: parseInt(e.target.value) || 0
                              })}
                              onFocus={(e) => {
                                e.target.select();
                              }}
                            />
                          </InputGroup>
                        )}

                        <InputGroup>
                          <Label>설명</Label>
                          <Textarea
                            value={editingItem.memo}
                            onChange={(e) => {
                              setEditingItem({ ...editingItem, memo: e.target.value });
                              // 자동 높이 조절
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            onFocus={(e) => {
                              if (e.target.value === '아이템 설명') {
                                e.target.select();
                              }
                              // 포커스 시에도 높이 조절
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            rows={1}
                          />
                        </InputGroup>

                        <ButtonGroup>
                          <CancelButton data-variant="modal-secondary" onClick={handleCancelEdit}>취소</CancelButton>
                          <SaveButton onClick={() => handleSaveItemCard(editingItem)}>확인</SaveButton>
                          <DeleteButton 
                            onClick={() => handleDeleteItemCard(item.itemCardId, activeTab)}
                            disabled={grantedItemCards.has(item.itemCardId)}
                            title={grantedItemCards.has(item.itemCardId) ? '학생들에게 지급된 아이템 카드는 삭제할 수 없습니다.' : '아이템 카드 삭제'}
                          >
                            삭제
                          </DeleteButton>
                        </ButtonGroup>
                      </EditForm>
                    ) : (
                      <>
                        <IconArea>
                          {item.cardImage === 'default' ? (
                            <PlaceholderIcon>
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                              </svg>
                            </PlaceholderIcon>
                          ) : item.cardImage && isNumericCardImage(item.cardImage) ? (
                            <img src={getIconBasicImagePath(item.cardImage)} alt={item.name} />
                          ) : item.cardImage ? (
                            <img src={item.cardImage} alt={item.name} />
                          ) : item.icon ? (
                            <img src={item.icon} alt={item.name} />
                          ) : (
                            <PlaceholderIcon>
                              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                                <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                              </svg>
                            </PlaceholderIcon>
                          )}
                        </IconArea>
                        <ItemName>{item.name}</ItemName>
                        <ItemMemo>{item.memo}</ItemMemo>
                        {(item.category === 'store' && item.cost !== 'Nocharge') || (item.type === 'active' && item.useCount !== undefined) ? (
                          <ItemMetaGroup>
                            {item.category === 'store' && item.cost !== 'Nocharge' && (
                              <ItemCost>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                  <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                  <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                </svg>
                                <span>X{item.cost}</span>
                              </ItemCost>
                            )}
                            {item.type === 'active' && item.useCount !== undefined && (
                              <UseCountBadge>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                                </svg>
                                <span>X{item.useCount}</span>
                              </UseCountBadge>
                            )}
                          </ItemMetaGroup>
                        ) : null}
                      </>
                    )}
                  </ItemCardBlock>
                ))}
                
                <AddItemButton onClick={handleAddItemCard}>
                  <AddIconCircle>
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="24" cy="24" r="23" stroke="var(--color-accent-strong)" strokeWidth="2" strokeDasharray="4 4" fill="none"/>
                      <path d="M24 14V34M14 24H34" stroke="var(--color-accent-strong)" strokeWidth="3" strokeLinecap="round"/>
                    </svg>
                  </AddIconCircle>
                  <AddText>아이템 추가</AddText>
                </AddItemButton>
                
                {isImportButtonEnabled && (
                  <ImportItemButton onClick={handleOpenImportModal}>
                    <AddIconCircle>
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="24" cy="24" r="23" stroke="var(--color-accent-strong)" strokeWidth="2" strokeDasharray="4 4" fill="none"/>
                        <path d="M24 14V34M14 24H34" stroke="var(--color-accent-strong)" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    </AddIconCircle>
                    <ImportText>다른 클래스<br/>데이터 가져오기</ImportText>
                  </ImportItemButton>
                )}
              </ItemCardGrid>
            )}
          </ContentArea>

          {/* 하단 [닫기] [저장] 버튼 */}
          <ItemCardModalFooter>
            <ItemCardModalFooterButton data-variant="modal-secondary" type="button" onClick={handleClose}>
              닫기
            </ItemCardModalFooterButton>
            <ItemCardModalFooterSaveButton type="button" onClick={handleSaveAll} disabled={isSaving}>
              {isSaving ? '저장 중...' : '저장'}
            </ItemCardModalFooterSaveButton>
          </ItemCardModalFooter>
        </ModalContent>
      </ModalOverlay>

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={executeDeleteItemCard}
        title="이 아이템 카드를 삭제하시겠습니까?"
      />

      {/* 카드 이미지 선택 모달 */}
      {isCardImageModalOpen && editingItem && (
        <CardImageModalOverlay onClick={() => setIsCardImageModalOpen(false)}>
          <CardImageModalContent onClick={(e) => e.stopPropagation()}>
            <CardImageModalHeader>
              <CardImageModalTitle>카드 이미지 선택</CardImageModalTitle>
              <CardImageModalCloseButton onClick={() => setIsCardImageModalOpen(false)}>×</CardImageModalCloseButton>
            </CardImageModalHeader>
            <CardImageModalBody>
              <CardImageTabRow>
                <CardImageTabButton
                  type="button"
                  $isActive={cardImageTab === 'none'}
                  onClick={() => {
                    setCardImageTab('none');
                    setSelectedCardImage('');
                  }}
                >
                  이미지 없음
                </CardImageTabButton>
                <CardImageTabButton
                  type="button"
                  $isActive={cardImageTab === 'default'}
                  onClick={() => setCardImageTab('default')}
                >
                  기본 이미지
                </CardImageTabButton>
                <CardImageTabButton
                  type="button"
                  $isActive={cardImageTab === 'user'}
                  onClick={() => setCardImageTab('user')}
                >
                  사용자 이미지
                </CardImageTabButton>
              </CardImageTabRow>
              <CardImageModalApplyButton
                type="button"
                onClick={() => {
                  if (editingItem) {
                    setEditingItem({ ...editingItem, cardImage: selectedCardImage });
                    setIsCardImageModalOpen(false);
                  }
                }}
              >
                선택 적용하기
              </CardImageModalApplyButton>

              {cardImageTab === 'none' ? null : cardImageTab === 'default' ? (
              <CardImageGrid>
                {cardImageOptions.map((option) => (
                  <CardImageOption
                    key={option}
                    $isSelected={selectedCardImage === option}
                    onClick={() => {
                      const now = Date.now();
                      const lastValue = lastCardImageClickValueRef.current;
                      const lastTime = lastCardImageClickTimeRef.current;
                      const isDoubleClick = lastValue === option && now - lastTime < 350;

                      setSelectedCardImage(option);

                      if (isDoubleClick && editingItem) {
                        setEditingItem({ ...editingItem, cardImage: option });
                        setIsCardImageModalOpen(false);
                        lastCardImageClickValueRef.current = null;
                        lastCardImageClickTimeRef.current = 0;
                      } else {
                        lastCardImageClickValueRef.current = option;
                        lastCardImageClickTimeRef.current = now;
                      }
                    }}
                  >
                    <CardImageOptionImg src={getIconBasicImagePath(option)} alt={`기본 이미지 ${option}`} />
                  </CardImageOption>
                ))}
              </CardImageGrid>
              ) : (
                <UserCardImageSection>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleUserImageDragEnd}
                  >
                    <SortableContext
                      items={userCardImagesBySlot.map((_, idx) => idx.toString())}
                    >
                      <UserCardImageGrid>
                        {userCardImagesBySlot.map((url, idx) => (
                          <SortableUserCardImageSlot
                            key={idx}
                            id={idx}
                            url={url}
                            index={idx}
                            isSelected={!!url && selectedCardImage === url}
                            onSelect={() => {
                              if (!url) return;
                              setSelectedCardImage(url);
                            }}
                            isImageInUse={isImageInUse}
                            onDelete={(imageUrl, slotIndex) => {
                              setDeleteConfirmImageUrl(imageUrl);
                              setDeleteConfirmSlotIndex(slotIndex);
                            }}
                            onReplace={handleReplaceUserImage}
                            onAdd={(slotIndex) => {
                              openUserImageSourceMenu(slotIndex, true);
                            }}
                          />
                        ))}
                      </UserCardImageGrid>
                    </SortableContext>
                  </DndContext>
                </UserCardImageSection>
              )}
            </CardImageModalBody>
          </CardImageModalContent>
        </CardImageModalOverlay>
      )}

      {/* 사용자 이미지 업로드/사진찍기 선택 모달 */}
      {isUserImageSourceMenuOpen && (
        <UserImageSourceOverlay onClick={() => {
          setIsUserImageSourceMenuOpen(false);
          setReplacingSlotIndex(null);
          setAddingSlotIndex(null);
        }}>
          <UserImageSourceContent onClick={(e) => e.stopPropagation()}>
            <UserImageSourceTitle>
              {replacingSlotIndex !== null ? '이미지 교체' : '이미지 추가'}
            </UserImageSourceTitle>
            <UserImageSourceButtons>
              <UserImageSourceButton type="button" onClick={handleStartUserFileUpload}>
                파일 업로드
              </UserImageSourceButton>
              <UserImageSourceButton type="button" onClick={handleStartUserCamera}>
                사진찍기
              </UserImageSourceButton>
            </UserImageSourceButtons>
          </UserImageSourceContent>
        </UserImageSourceOverlay>
      )}

      {/* 이미지 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteConfirmImageUrl !== null}
        title={['이미지 삭제', '이 이미지를 삭제하시겠습니까?']}
        onClose={() => {
          setDeleteConfirmImageUrl(null);
          setDeleteConfirmSlotIndex(null);
        }}
        onConfirm={async () => {
          if (deleteConfirmImageUrl && deleteConfirmSlotIndex !== null) {
            try {
              await handleDeleteUserImage(deleteConfirmImageUrl, deleteConfirmSlotIndex);
            } catch (error) {
              // 에러는 handleDeleteUserImage에서 이미 처리됨
              console.error('이미지 삭제 중 오류:', error);
            }
          }
          setDeleteConfirmImageUrl(null);
          setDeleteConfirmSlotIndex(null);
        }}
        onCancel={() => {
          setDeleteConfirmImageUrl(null);
          setDeleteConfirmSlotIndex(null);
        }}
      />

      {/* 사용자 이미지 파일 input */}
      <input
        ref={userFileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          // 같은 파일 재선택 가능하도록 초기화
          e.target.value = '';
          if (!f) return;
          void handleSelectUserImageFile(f);
        }}
      />

      {/* 사용자 카메라 모달 */}
      {isUserCameraModalOpen && (
        <MediaModalOverlay onClick={() => setIsUserCameraModalOpen(false)}>
          <MediaModalContent onClick={(e) => e.stopPropagation()}>
            <MediaModalTitle>사진 찍기</MediaModalTitle>
            <div style={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', background: '#111', borderRadius: 12, overflow: 'hidden' }}>
              <video ref={userVideoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} playsInline muted />
            </div>
            <MediaModalButtonRow>
              <MediaModalButton type="button" onClick={() => setIsUserCameraModalOpen(false)}>
                취소
              </MediaModalButton>
              <MediaModalPrimaryButton type="button" onClick={handleCaptureUserFromCamera}>
                촬영
              </MediaModalPrimaryButton>
            </MediaModalButtonRow>
          </MediaModalContent>
        </MediaModalOverlay>
      )}

      {/* 사용자 이미지 크롭 모달 (정사각형) */}
      {isUserCropModalOpen && (
        <MediaModalOverlay onClick={() => setIsUserCropModalOpen(false)}>
          <CropModalContent onClick={(e) => e.stopPropagation()}>
            <MediaModalTitle>이미지 크롭</MediaModalTitle>
            <CropContainer>
              <Cropper
                image={rawUserImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropSize={{ width: 144, height: 144 }}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, croppedAreaPixels) => {
                  userCropPixelsRef.current = croppedAreaPixels as CropAreaPixels;
                }}
                minZoom={0.5}
                maxZoom={3}
              />
            </CropContainer>
            <CropZoomSliderContainer>
              <CropZoomSliderLabel>확대/축소</CropZoomSliderLabel>
              <CropZoomSlider
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </CropZoomSliderContainer>
            <CropModalButtonRow>
              <MediaModalButton type="button" onClick={() => setIsUserCropModalOpen(false)} disabled={isUploadingUserCardImage}>
                취소
              </MediaModalButton>
              <MediaModalPrimaryButton type="button" onClick={handleUploadCroppedUserCardImage} disabled={isUploadingUserCardImage}>
                {isUploadingUserCardImage ? '업로드 중...' : '등록'}
              </MediaModalPrimaryButton>
            </CropModalButtonRow>
          </CropModalContent>
        </MediaModalOverlay>
      )}

      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage('')} duration={1100} />}
      
      {/* 모달 닫기 확인 팝업 */}
      <ConfirmModal
        isOpen={isCloseConfirmModalOpen}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={['변경된 데이터가 있습니다.', '팝업을 닫으시겠습니까?']}
        confirmText="닫기"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-pending)"
      />
      
      {/* 라이센스 제한 모달 */}
      <LicenseLimitModal
        isOpen={showLicenseLimitModal}
        message={licenseLimitMessage}
        onClose={() => setShowLicenseLimitModal(false)}
        onUpgrade={() => {
          setShowLicenseLimitModal(false);
          onLicenseClick?.();
        }}
      />
      
      {/* 다른 클래스 데이터 가져오기 모달 */}
      {showImportModal && (
        <ImportModalOverlay onClick={handleCloseImportModal}>
          <ImportModalContent onClick={(e) => e.stopPropagation()}>
            <ImportModalHeader>
              <ImportModalTitle>다른 클래스 데이터 가져오기</ImportModalTitle>
              <ImportModalCloseButton onClick={handleCloseImportModal}>×</ImportModalCloseButton>
            </ImportModalHeader>
            
            <ImportModalBody>
              {/* 클래스 선택 드롭다운 */}
              <ImportSection>
                <ImportLabel>클래스 선택</ImportLabel>
                <ImportClassSelectContainer>
                  <ImportClassSelectButton
                    onClick={() => setIsClassDropdownOpen(!isClassDropdownOpen)}
                    type="button"
                  >
                    <ImportClassSelectText>
                      {selectedSourceClassId 
                        ? availableClasses.find(c => c.id === selectedSourceClassId)?.name || '클래스를 선택하세요'
                        : '클래스를 선택하세요'}
                    </ImportClassSelectText>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </ImportClassSelectButton>
                  {isClassDropdownOpen && (
                    <>
                      <ImportClassDropdownOverlay onClick={() => setIsClassDropdownOpen(false)} />
                      <ImportClassDropdown>
                        <ImportClassDropdownOption
                          onClick={() => {
                            setSelectedSourceClassId('');
                            setIsClassDropdownOpen(false);
                            setSourceLevelupItems([]);
                            setSourceAssignmentItems([]);
                            setSourceStoreItems([]);
                            setSelectedItemCardIds(new Set());
                          }}
                        >
                          클래스를 선택하세요
                        </ImportClassDropdownOption>
                        {availableClasses.map(cls => (
                          <ImportClassDropdownOption
                            key={cls.id}
                            onClick={() => handleSourceClassChange(cls.id)}
                          >
                            {cls.name}
                          </ImportClassDropdownOption>
                        ))}
                      </ImportClassDropdown>
                    </>
                  )}
                </ImportClassSelectContainer>
              </ImportSection>
              
              {/* 아이템 카드 목록 */}
              {isLoadingSourceItems ? (
                <ImportLoadingText>아이템 카드를 불러오는 중...</ImportLoadingText>
              ) : selectedSourceClassId ? (
                <>
                  {/* 레벨업 아이템 */}
                  <ImportSection>
                    <ImportLabel>레벨업 아이템</ImportLabel>
                    {sourceLevelupItems.length > 0 ? (
                      <ImportItemSelectGrid>
                        {sourceLevelupItems.map(item => {
                          const isSelected = selectedItemCardIds.has(item.itemCardId);
                          return (
                            <ImportItemCardSelectBlock
                              key={item.itemCardId}
                              $isSelected={isSelected}
                              onClick={() => handleToggleItemSelection(item.itemCardId)}
                            >
                              <ImportItemCardIconArea>
                                {item.cardImage === 'default' || (!item.cardImage && !item.icon) ? (
                                  <PlaceholderIcon>
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                      <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                      <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                    </svg>
                                  </PlaceholderIcon>
                                ) : item.cardImage && isNumericCardImage(item.cardImage) ? (
                                  <img src={getIconBasicImagePath(item.cardImage)} alt={item.name} />
                                ) : item.cardImage ? (
                                  <img src={item.cardImage} alt={item.name} />
                                ) : item.icon ? (
                                  <img src={item.icon} alt={item.name} />
                                ) : (
                                  <PlaceholderIcon>
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                                      <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                                    </svg>
                                  </PlaceholderIcon>
                                )}
                              </ImportItemCardIconArea>
                              <ImportItemCardName>{item.name}</ImportItemCardName>
                              <ImportItemCardMemo>{item.memo || ''}</ImportItemCardMemo>
                              {((item.category === 'store' && item.cost !== 'Nocharge') || (item.type === 'active' && item.useCount !== undefined)) && (
                                <ImportItemCardMetaGroup>
                                  {item.category === 'store' && item.cost !== 'Nocharge' && (
                                    <ImportItemCardCost>
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                        <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                        <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                      </svg>
                                      <span>X{item.cost}</span>
                                    </ImportItemCardCost>
                                  )}
                                  {item.type === 'active' && item.useCount !== undefined && (
                                    <ImportItemCardUseCount>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                                      </svg>
                                      <span>X{item.useCount}</span>
                                    </ImportItemCardUseCount>
                                  )}
                                </ImportItemCardMetaGroup>
                              )}
                              {isSelected && (
                                <ImportSelectedBadge>✓</ImportSelectedBadge>
                              )}
                            </ImportItemCardSelectBlock>
                          );
                        })}
                      </ImportItemSelectGrid>
                    ) : (
                      <ImportEmptyBlock>
                        등록된 레벨업 아이템이 없습니다
                      </ImportEmptyBlock>
                    )}
                  </ImportSection>
                  
                  {/* 과제보상 아이템 */}
                  <ImportSection>
                    <ImportLabel>과제보상 아이템</ImportLabel>
                    {sourceAssignmentItems.length > 0 ? (
                      <ImportItemSelectGrid>
                        {sourceAssignmentItems.map(item => {
                          const isSelected = selectedItemCardIds.has(item.itemCardId);
                          return (
                            <ImportItemCardSelectBlock
                              key={item.itemCardId}
                              $isSelected={isSelected}
                              onClick={() => handleToggleItemSelection(item.itemCardId)}
                            >
                              <ImportItemCardIconArea>
                                {item.cardImage === 'default' || (!item.cardImage && !item.icon) ? (
                                  <PlaceholderIcon>
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                      <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                      <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                    </svg>
                                  </PlaceholderIcon>
                                ) : item.cardImage && isNumericCardImage(item.cardImage) ? (
                                  <img src={getIconBasicImagePath(item.cardImage)} alt={item.name} />
                                ) : item.cardImage ? (
                                  <img src={item.cardImage} alt={item.name} />
                                ) : item.icon ? (
                                  <img src={item.icon} alt={item.name} />
                                ) : (
                                  <PlaceholderIcon>
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                                      <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                                    </svg>
                                  </PlaceholderIcon>
                                )}
                              </ImportItemCardIconArea>
                              <ImportItemCardName>{item.name}</ImportItemCardName>
                              <ImportItemCardMemo>{item.memo || ''}</ImportItemCardMemo>
                              {((item.category === 'store' && item.cost !== 'Nocharge') || (item.type === 'active' && item.useCount !== undefined)) && (
                                <ImportItemCardMetaGroup>
                                  {item.category === 'store' && item.cost !== 'Nocharge' && (
                                    <ImportItemCardCost>
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                        <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                        <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                      </svg>
                                      <span>X{item.cost}</span>
                                    </ImportItemCardCost>
                                  )}
                                  {item.type === 'active' && item.useCount !== undefined && (
                                    <ImportItemCardUseCount>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                                      </svg>
                                      <span>X{item.useCount}</span>
                                    </ImportItemCardUseCount>
                                  )}
                                </ImportItemCardMetaGroup>
                              )}
                              {isSelected && (
                                <ImportSelectedBadge>✓</ImportSelectedBadge>
                              )}
                            </ImportItemCardSelectBlock>
                          );
                        })}
                      </ImportItemSelectGrid>
                    ) : (
                      <ImportEmptyBlock>
                        등록된 과제보상 아이템이 없습니다
                      </ImportEmptyBlock>
                    )}
                  </ImportSection>
                  
                  {/* 스토어 아이템 */}
                  <ImportSection>
                    <ImportLabel>스토어 아이템</ImportLabel>
                    {sourceStoreItems.length > 0 ? (
                      <ImportItemSelectGrid>
                        {sourceStoreItems.map(item => {
                          const isSelected = selectedItemCardIds.has(item.itemCardId);
                          return (
                            <ImportItemCardSelectBlock
                              key={item.itemCardId}
                              $isSelected={isSelected}
                              onClick={() => handleToggleItemSelection(item.itemCardId)}
                            >
                              <ImportItemCardIconArea>
                                {item.cardImage === 'default' || (!item.cardImage && !item.icon) ? (
                                  <PlaceholderIcon>
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                      <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                      <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                                    </svg>
                                  </PlaceholderIcon>
                                ) : item.cardImage && isNumericCardImage(item.cardImage) ? (
                                  <img src={getIconBasicImagePath(item.cardImage)} alt={item.name} />
                                ) : item.cardImage ? (
                                  <img src={item.cardImage} alt={item.name} />
                                ) : item.icon ? (
                                  <img src={item.icon} alt={item.name} />
                                ) : (
                                  <PlaceholderIcon>
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                                      <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                                    </svg>
                                  </PlaceholderIcon>
                                )}
                              </ImportItemCardIconArea>
                              <ImportItemCardName>{item.name}</ImportItemCardName>
                              <ImportItemCardMemo>{item.memo || ''}</ImportItemCardMemo>
                              {((item.category === 'store' && item.cost !== 'Nocharge') || (item.type === 'active' && item.useCount !== undefined)) && (
                                <ImportItemCardMetaGroup>
                                  {item.category === 'store' && item.cost !== 'Nocharge' && (
                                    <ImportItemCardCost>
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="10" fill="#f39c12"/>
                                        <circle cx="12" cy="12" r="8" fill="#f1c40f"/>
                                        <text x="12" y="16" fontSize="12" fontWeight="bold" fill="#e67e22" textAnchor="middle">₩</text>
                                      </svg>
                                      <span>X{item.cost}</span>
                                    </ImportItemCardCost>
                                  )}
                                  {item.type === 'active' && item.useCount !== undefined && (
                                    <ImportItemCardUseCount>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5C22 12.27 18.6 15.36 13.45 20.03L12 21.35Z" fill="#ff4757"/>
                                      </svg>
                                      <span>X{item.useCount}</span>
                                    </ImportItemCardUseCount>
                                  )}
                                </ImportItemCardMetaGroup>
                              )}
                              {isSelected && (
                                <ImportSelectedBadge>✓</ImportSelectedBadge>
                              )}
                            </ImportItemCardSelectBlock>
                          );
                        })}
                      </ImportItemSelectGrid>
                    ) : (
                      <ImportEmptyBlock>
                        등록된 스토어 아이템이 없습니다
                      </ImportEmptyBlock>
                    )}
                  </ImportSection>
                </>
              ) : null}
            </ImportModalBody>
            
            <ImportModalFooter>
              <ImportModalButton onClick={handleCloseImportModal} $variant="secondary">
                닫기
              </ImportModalButton>
              <ImportModalButton
                onClick={handleApplySelectedItems}
                $variant="primary"
                disabled={selectedItemCardIds.size === 0}
              >
                <span className="button-text-full">현재 클래스에 반영</span>
                <span className="button-text-short">클래스에 반영</span>
              </ImportModalButton>
            </ImportModalFooter>
          </ImportModalContent>
        </ImportModalOverlay>
      )}
    </>
  );
};

export default ItemCardSettingsModal;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 80px 20px;
  overflow-y: auto;
  min-height: 100vh;
  
  @media (max-width: 600px) {
    padding: 60px 20px;
  }
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  overflow: visible;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text-primary);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: var(--color-text-gray);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-border);
`;

const TabButton = styled.button<{ $isActive: boolean; $isDisabled?: boolean }>`
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: ${props => props.$isDisabled ? 'var(--color-disabled-bg)' :
                props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
  color: ${props => props.$isDisabled ? 'var(--color-disabled-text)' :
                props.$isActive ? 'white' : 'var(--color-text-secondary)'};
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$isDisabled ? 'var(--color-disabled-bg)' :
                  props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: -2px;
  }
`;

const ContentArea = styled.div`
  padding: 24px;
  min-height: 400px;
`;

// 프로필 카드 탭 스타일 (아이템 카드와 동일한 상단 여백 - ContentArea padding 24px)
const ProfileCardTabContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 0 20px 40px;
  gap: 40px;
`;

const ProfileCardSetsRow = styled.div`
  display: flex;
  gap: 40px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ProfileCardSetItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: var(--color-panel);
  border-radius: 16px;
  border: 2px solid var(--color-border);
`;

const ProfileCardSetTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--color-accent-text);
`;

const ProfileCardStackWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProfileCardStack = styled.div`
  position: relative;
  width: 140px;
  height: 180px;
`;

const ProfileCardBack = styled.div<{ $index: number }>`
  position: absolute;
  width: 120px;
  height: 160px;
  background: linear-gradient(135deg, var(--color-border) 0%, var(--color-border) 100%);
  border-radius: 12px;
  border: 2px solid var(--color-border);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  ${props => props.$index === 1 && `
    top: 5px;
    left: 5px;
    transform: rotate(-3deg);
  `}
  
  ${props => props.$index === 2 && `
    top: 10px;
    left: 10px;
    transform: rotate(-6deg);
  `}
`;

const ProfileCardFront = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 120px;
  height: 160px;
  background: linear-gradient(135deg, rgba(var(--color-accent-rgb), 0.9) 0%, var(--color-accent-strong) 100%);
  border-radius: 12px;
  border: 2px solid var(--color-accent-strong);
  box-shadow: 0 8px 24px rgba(var(--color-accent-rgb), 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(2deg);
  
  /* 다크 모드: 완전히 불투명하게, 테두리 흰색 */
  :root:not([data-theme='light']) & {
    background: linear-gradient(135deg, var(--color-accent-strong) 0%, var(--color-accent-strong) 100%);
    border: 2px solid rgba(255, 255, 255, 0.8);
  }
`;

const ProfileCardQuestion = styled.span`
  font-size: 56px;
  font-weight: bold;
  color: white;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const ProfileCardCostInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProfileCardCoinIcon = styled.div`
  display: flex;
  align-items: center;
`;

const ProfileCardCostInput = styled.input`
  width: 120px;
  padding: 12px 16px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  outline: none;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    border-color: var(--color-accent-text);
  }
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const ProfileCardCostDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6c757d;
  text-align: center;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--color-text-gray);
`;

const ItemCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 180px);
  gap: 20px;
  justify-content: center;

  @media (max-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const ItemCardBlock = styled.div<{ $isEditing?: boolean }>`
  width: 180px;
  height: 300px;
  border: 2px solid ${props => props.$isEditing ? 'var(--color-accent-strong)' : 'var(--color-accent-strong)'};
  border-radius: 12px;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${props => props.$isEditing ? '4px' : '10%'};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  &:hover {
    transform: ${props => props.$isEditing ? 'none' : 'translateY(-4px)'};
    box-shadow: ${props => props.$isEditing ? 'none' : '0 8px 16px rgba(0, 0, 0, 0.15)'};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const IconArea = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-panel);
  border: 2px solid var(--color-accent-strong);

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemName = styled.div`
  width: 100%;
  margin-top: 8%;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.3;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMemo = styled.div`
  width: 100%;
  margin-top: 4%;
  font-size: 11px;
  color: #999;
  text-align: center;
  word-break: break-word;
  line-height: 1.4;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0;
`;

const ItemMetaGroup = styled.div`
  width: 100%;
  margin-top: 6%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-shrink: 0;
`;

const ItemCost = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 18px;
    height: 18px;
  }

  span {
    font-size: 16px;
    font-weight: 700;
    color: #f39c12;
    line-height: 1;
  }
`;

const UseCountBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 16px;
    height: 16px;
  }

  span {
    font-size: 16px;
    font-weight: 700;
    color: #ff4757;
    line-height: 1;
  }
`;

const AddItemButton = styled.div`
  width: 180px;
  height: 300px;
  border: 2px dashed var(--color-accent-strong);
  border-radius: 12px;
  background: rgba(var(--color-accent-rgb), 0.02);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 8px;
  box-sizing: border-box;

  &:hover {
    background: var(--color-accent-soft);
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AddIconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddText = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--color-accent-text);
`;

const ImportItemButton = styled.div`
  width: 180px;
  height: 300px;
  border: 2px dashed var(--color-accent-strong);
  border-radius: 12px;
  background: rgba(var(--color-accent-rgb), 0.02);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 8px;
  box-sizing: border-box;

  &:hover {
    background: var(--color-accent-soft);
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ImportText = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--color-accent-text);
  text-align: center;
  line-height: 1.4;
`;

// 다른 클래스 데이터 가져오기 모달 스타일
const ImportModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 10000;
  overflow-y: auto;
  padding: 60px 20px;
`;

const ImportModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  margin: 0 auto;
`;

const ImportModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const ImportModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
`;

const ImportModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-primary);
  }
`;

const ImportModalBody = styled.div`
  padding: 24px;
`;

const ImportSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ImportLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 12px;
`;

const ImportClassSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ImportClassSelectButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--color-accent-text);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  svg {
    flex-shrink: 0;
    color: var(--color-text-secondary);
  }
`;

const ImportClassSelectText = styled.span`
  flex: 1;
  text-align: left;
`;

const ImportClassDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
`;

const ImportClassDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  margin-top: 4px;
  overflow: visible;
`;

const ImportClassDropdownOption = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;
  color: var(--color-text-primary);
  text-align: left;
  
  &:hover {
    background-color: var(--color-panel);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
`;

const ImportLoadingText = styled.div`
  text-align: center;
  color: var(--color-text-muted);
  padding: 40px 0;
  font-size: 14px;
`;

const ImportEmptyBlock = styled.div`
  width: 100%;
  padding: 16px;
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 14px;
  background: var(--color-surface);
  margin-top: 12px;
`;

const ImportItemSelectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 120px);
  gap: 14px;
  justify-content: center;
  margin-top: 12px;

  @media (max-width: 768px) {
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const ImportItemCardSelectBlock = styled.div<{ $isSelected?: boolean }>`
  width: 120px;
  height: 200px;
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-accent-strong)'};
  border-radius: 8px;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10%;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'rgba(255, 255, 255, 0.8)'};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ImportItemCardIconArea = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-panel);
  border: 2px solid var(--color-accent-strong);

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ImportItemCardName = styled.div`
  width: 100%;
  margin-top: 8%;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.3;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ImportItemCardMemo = styled.div`
  width: 100%;
  margin-top: 4%;
  margin-bottom: auto;
  font-size: 8px;
  color: #999;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0;
`;

const ImportItemCardMetaGroup = styled.div`
  width: 100%;
  margin-top: 6%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ImportItemCardCost = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;

  svg {
    width: 14px;
    height: 14px;
  }

  span {
    font-size: 12px;
    font-weight: 700;
    color: #f39c12;
    line-height: 1;
  }
`;

const ImportItemCardUseCount = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;

  svg {
    width: 12px;
    height: 12px;
  }

  span {
    font-size: 12px;
    font-weight: 700;
    color: #ff4757;
    line-height: 1;
  }
`;

const ImportSelectedBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
`;

const ItemCardModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--color-border);
`;

const ItemCardModalFooterButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);

  &:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-text-muted);
  }
`;

const ItemCardModalFooterSaveButton = styled.button`
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--color-accent-strong);
  color: white;
  border: none;

  &:hover:not(:disabled) {
    background: var(--color-accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImportModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--color-border);
`;

const ImportModalButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  .button-text-full {
    display: inline;
  }

  .button-text-short {
    display: none;
  }

  @media (max-width: 480px) {
    .button-text-full {
      display: none;
    }

    .button-text-short {
      display: inline;
    }
  }
  
  ${props => props.$variant === 'primary' ? `
    background: var(--color-accent-strong);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--color-accent);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
    
    &:disabled {
      background: var(--color-border);
      color: var(--color-text-muted);
      cursor: not-allowed;
      opacity: 0.5;
    }
  ` : `
    background: var(--color-border);
    color: var(--color-text-primary);
    
    &:hover {
      background: var(--color-text-muted);
      color: white;
    }
  `}
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

// Edit Form Styles
const EditForm = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px;
  box-sizing: border-box;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
`;

const Input = styled.input`
  padding: 4px 6px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  background: var(--color-surface);
  color: var(--color-text-primary);

  &:focus {
    border-color: var(--color-accent-text);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const Textarea = styled.textarea`
  padding: 4px 6px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.2s;
  resize: none;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
  min-height: 40px;
  line-height: 1.4;
  background: var(--color-surface);
  color: var(--color-text-primary);

  &:focus {
    border-color: var(--color-accent-text);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  margin-top: auto;
  flex-wrap: wrap;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 4px 6px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:hover {
    background: var(--color-panel);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 4px 6px;
  background: var(--color-accent-strong);
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.9);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const DeleteButton = styled.button`
  flex: 1;
  padding: 4px 6px;
  background: #dc3545;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
  min-width: 0;

  &:hover {
    background: #c82333;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 카드 이미지 버튼 스타일
const CardImageButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 6px;
`;

const CardImageButton = styled.button`
  width: 100px;
  height: 100px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface-highlight);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  overflow: hidden;

  &:hover {
    border-color: var(--color-accent-text);
    background: #eee;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CardImagePlaceholder = styled.span`
  font-size: 36px;
  font-weight: bold;
  color: #999;
`;

// 타입 토글 버튼 스타일 (번호/자리 토글 스타일 기반)
const TypeToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 6px;
`;

const TypeToggleButton = styled.div<{ $isActive: boolean }>`
  display: flex;
  background: var(--color-accent-strong);
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 26px;
  min-width: 100px;
  width: auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isActive ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: var(--color-surface);
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }
`;

const TypeToggleOption = styled.div<{ $isActive: boolean }>`
  flex: 1;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'rgba(255, 255, 255, 0.7)'};
  text-align: center;
  z-index: 2;
  transition: color 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* 다크모드: 선택된 옵션은 흰색, 선택되지 않은 옵션은 회색 */
  :root:not([data-theme='light']) & {
    color: ${props => props.$isActive ? '#ffffff' : 'var(--color-text-gray)'};
  }
`;

// 카드 이미지 선택 모달 스타일
const CardImageModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 2000;
  overflow-y: auto;
  padding: 80px 20px;
`;

const CardImageModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  overflow: hidden;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  margin-bottom: 80px;
`;

const CardImageModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-panel);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
`;

const CardImageModalTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const CardImageModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--color-text-muted);
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: var(--color-border);
    color: var(--color-text-dark-gray);
  }
`;

const CardImageModalBody = styled.div`
  padding: 32px 40px;
`;

const CardImageModalApplyButton = styled.button`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 24px;
  padding: 12px 32px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-accent-text);
    transform: translateY(-1px);
    box-shadow: var(--modal-shadow);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CardImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 144px);
  gap: 20px;
  justify-content: center;
  
  @media (max-width: 820px) {
    grid-template-columns: repeat(3, 144px);
    gap: 16px;
  }
  
  @media (max-width: 520px) {
    grid-template-columns: repeat(2, 144px);
    gap: 12px;
  }
`;

const CardImageOption = styled.button<{ $isSelected?: boolean }>`
  width: 144px;
  height: 144px;
  border: 3px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 10px;
  background: ${props => props.$isSelected ? 'rgba(var(--color-accent-rgb), 0.1)' : 'var(--color-surface-highlight)'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;

  &:hover {
    border-color: var(--color-accent-text);
    background: var(--color-accent-soft);
    transform: scale(1.02);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CardImageOptionImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 7px;
`;

const CardImagePreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardImageTabRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 18px;
`;

const CardImageTabButton = styled.button<{ $isActive: boolean }>`
  border: 2px solid ${({ $isActive }) => ($isActive ? 'var(--color-accent-strong)' : 'var(--color-border)')};
  background: ${({ $isActive }) => ($isActive ? 'rgba(var(--color-accent-rgb), 0.12)' : 'transparent')};
  color: var(--color-text-primary);
  padding: 10px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-accent-strong);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const UserCardImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UserCardImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 144px);
  gap: 20px;
  justify-content: center;
  
  @media (max-width: 820px) {
    grid-template-columns: repeat(3, 144px);
    gap: 16px;
  }
  
  @media (max-width: 520px) {
    grid-template-columns: repeat(2, 144px);
    gap: 12px;
  }
`;

const UserCardImageSlot = styled.button<{ $isFilled: boolean; $isSelected: boolean; $isDragging?: boolean; $isDragMode?: boolean }>`
  position: relative;
  width: 144px;
  height: 144px;
  border-radius: 10px;
  border: ${({ $isSelected, $isFilled }) => {
    if ($isSelected) return '3px solid var(--color-accent-strong)';
    if ($isFilled) return '3px solid var(--color-border)';
    return '2px dashed var(--color-accent-strong)';
  }};
  background: ${({ $isFilled, $isSelected }) => {
    if ($isSelected) return 'rgba(var(--color-accent-rgb), 0.1)';
    if ($isFilled) return 'var(--color-surface-highlight)';
    return 'transparent';
  }};
  overflow: hidden;
  padding: 0;
  cursor: ${({ $isFilled, $isDragMode }) => {
    if ($isDragMode) return 'grabbing';
    return 'pointer';
  }};
  transition: transform 0.2s, border-color 0.2s, background 0.2s, box-shadow 0.2s;
  touch-action: none;
  box-shadow: ${({ $isDragging }) => 
    $isDragging 
      ? '0 8px 24px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2)' 
      : 'none'
  };

  /* 다크 모드: 회색 테두리 */
  :root:not([data-theme='light']) & {
    border: ${({ $isSelected, $isFilled }) => {
      if ($isSelected) return '3px solid var(--color-accent-strong)';
      if ($isFilled) return '3px solid #666';
      return '2px dashed rgba(255, 255, 255, 0.8)';
    }};
  }

  &:active {
    cursor: ${({ $isFilled, $isDragMode }) => {
      if (!$isFilled) return 'default';
      if ($isDragMode) return 'grabbing';
      return 'pointer';
    }};
  }

  &:hover {
    ${({ $isFilled }) => ($isFilled ? `
      border-color: var(--color-accent-text);
      background: var(--color-accent-soft);
      transform: scale(1.02);
    ` : `
      border-color: var(--color-accent-strong);
      transform: scale(1.02);
    `)}
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const UserCardImageThumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const EmptySlotIcon = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  opacity: 0.6;
  transition: all 0.2s ease;

  svg {
    width: 48px;
    height: 48px;
  }
`;

const UserImageActionButton = styled.div<{ $isReplace?: boolean; $position?: 'top-left' | 'top-right' }>`
  position: absolute;
  top: 8px;
  ${({ $position }) => ($position === 'top-left' ? 'left: 8px;' : 'right: 8px;')}
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(var(--color-accent-rgb), 0.9);
  color: white;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  &:hover {
    background: var(--color-accent-strong);
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const UserImageSourceOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
  padding: 20px;
`;

const UserImageSourceContent = styled.div`
  width: 100%;
  max-width: 420px;
  background: var(--color-surface);
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  border-radius: 12px;
  padding: 18px;
`;

const UserImageSourceTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: var(--color-text-primary);
  margin-bottom: 12px;
`;

const UserImageSourceButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const UserImageSourceButton = styled.button`
  flex: 1;
  padding: 12px 14px;
  border-radius: 10px;
  border: 2px solid var(--color-border);
  background: var(--color-panel);
  color: var(--color-text-primary);
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-accent-strong);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const MediaModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2200;
  padding: 20px;
`;

const MediaModalContent = styled.div`
  width: min(720px, 96vw);
  background: var(--color-surface);
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MediaModalTitle = styled.div`
  font-size: 16px;
  font-weight: 900;
  color: var(--color-text-primary);
  text-align: center;
`;

const CropContainer = styled.div`
  position: relative;
  width: 288px;
  height: 288px;
  background: #111;
  border-radius: 12px;
  overflow: hidden;
  margin: 0 auto;
`;

const CropModalContent = styled.div`
  width: 324px; /* 288px + padding 18px * 2 */
  background: var(--color-surface);
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CropZoomSliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const CropZoomSliderLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
`;

const CropZoomSlider = styled.input`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: var(--color-border);
  outline: none;
  -webkit-appearance: none;
  appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--color-accent-strong);
    cursor: pointer;
    transition: background 0.2s;
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--color-accent-strong);
    cursor: pointer;
    border: none;
    transition: background 0.2s;
  }

  &:hover::-webkit-slider-thumb {
    background: var(--color-accent);
  }

  &:hover::-moz-range-thumb {
    background: var(--color-accent);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const CropModalButtonRow = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
`;

const MediaModalButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const MediaModalButton = styled.button`
  padding: 10px 14px;
  border-radius: 10px;
  border: 2px solid var(--color-border);
  background: transparent;
  color: var(--color-text-primary);
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  ${CropModalButtonRow} & {
    flex: 1;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    border-color: var(--color-accent-strong);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const MediaModalPrimaryButton = styled(MediaModalButton)`
  background: var(--color-accent-strong);
  border-color: var(--color-accent-strong);
  color: #fff;
`;

